/**
 * Project Production View
 *
 * Project-centric interface for production workers.
 * Groups work by quote, shows routing context, and highlights user's pending work.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import WorkCenterSelector from "./WorkCenterSelector";
import QuoteQueue from "./QuoteQueue";
import QuoteDetail from "./QuoteDetail";
import ProductionActionBar from "./ProductionActionBar";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Available work centers
const WORK_CENTERS = [
  { id: "PA", label: "Production Art" },
  { id: "OPTI", label: "OPTI/Finishing" },
  { id: "ASSEMBLY", label: "Assembly" },
  { id: "PAINT", label: "Paint" },
  { id: "3D MODEL", label: "3D Model" },
  { id: "PM", label: "PM" },
  { id: "QA", label: "QA" },
];

export default function ProjectProductionView({ onBackToApp, userEmail }) {
  // Work center selection (persisted to localStorage)
  const [selectedWorkCenters, setSelectedWorkCenters] = useState(() => {
    const saved = localStorage.getItem("3dcolor-production-workcenters");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return ["PA"];
      }
    }
    return ["PA"]; // Default
  });

  // Data state
  const [quotes, setQuotes] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [workCenterCounts, setWorkCenterCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // UI state
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState([]);

  // Timer tick for live updates
  const [tick, setTick] = useState(0);

  const getToken = () => sessionStorage.getItem("msal.backendToken");

  // Persist work center selection
  useEffect(() => {
    localStorage.setItem(
      "3dcolor-production-workcenters",
      JSON.stringify(selectedWorkCenters)
    );
  }, [selectedWorkCenters]);

  // Timer tick for live session updates
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch production queue data
  const fetchQueue = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    if (selectedWorkCenters.length === 0) {
      setQuotes([]);
      setPipeline([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const wcParam = selectedWorkCenters.join(",");
      const res = await fetch(
        `${API_BASE}/api/production/my-queue?workCenters=${encodeURIComponent(wcParam)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to load: ${res.status}`);
      }

      const data = await res.json();
      setQuotes(data.quotes || []);
      setPipeline(data.pipeline || []);
      setWorkCenterCounts(data.workCenterCounts || {});
      setLastUpdated(new Date(data.lastUpdated));
      setError(null);

      // Update selected quote if it still exists
      if (selectedQuote) {
        const updated = (data.quotes || []).find(
          (q) => q.quoteNumber === selectedQuote.quoteNumber
        );
        if (updated) {
          setSelectedQuote(updated);
        } else {
          setSelectedQuote(null);
          setSelectedJobs([]);
        }
      }
    } catch (err) {
      console.error("Production queue error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkCenters, selectedQuote?.quoteNumber]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchQueue();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // Toggle work center selection
  const handleToggleWorkCenter = (wcId) => {
    setSelectedWorkCenters((prev) => {
      if (prev.includes(wcId)) {
        return prev.filter((id) => id !== wcId);
      } else {
        return [...prev, wcId];
      }
    });
  };

  // Handle quote selection
  const handleSelectQuote = (quote) => {
    setSelectedQuote(quote);
    setSelectedJobs([]);
  };

  // Handle job selection (for batch operations)
  const handleToggleJob = (jobNumber) => {
    setSelectedJobs((prev) => {
      if (prev.includes(jobNumber)) {
        return prev.filter((j) => j !== jobNumber);
      } else {
        return [...prev, jobNumber];
      }
    });
  };

  const handleSelectAllJobs = (jobNumbers) => {
    setSelectedJobs(jobNumbers);
  };

  // Time tracking actions
  const handleStartJob = async (job, operationCode) => {
    const token = getToken();
    if (!token || !job) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${job.jobId}/time/start`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operationCode,
          workCenter: selectedWorkCenters[0], // Primary work center
          orderNumber: selectedQuote?.orderNumber,
          quoteNumber: selectedQuote?.quoteNumber,
          jobNumber: job.jobNumber,
          jobName: job.description,
        }),
      });

      if (!res.ok) throw new Error("Failed to start");

      // Refresh queue
      await fetchQueue();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error starting job:", err);
      alert("Failed to start job");
    }
  };

  const handlePauseJob = async (jobId) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/time/pause`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to pause");
      await fetchQueue();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error pausing job:", err);
    }
  };

  const handleResumeJob = async (jobId) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/time/resume`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to resume");
      await fetchQueue();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error resuming job:", err);
    }
  };

  const handleCompleteJob = async (jobId, piecesFinished = 0) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/time/complete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ piecesFinished }),
      });

      if (!res.ok) throw new Error("Failed to complete");

      await fetchQueue();
      window.dispatchEvent(new Event("time-updated"));
    } catch (err) {
      console.error("Error completing job:", err);
    }
  };

  // Format elapsed time
  const formatElapsed = useCallback((session) => {
    if (!session) return "0:00";

    const accumulated = Math.floor(session.accumulatedSeconds || 0);
    let seconds = 0;

    if (session.state === "running" && session.startTime) {
      const startMs = new Date(session.startTime).getTime();
      const currentSegment = Math.floor((Date.now() - startMs) / 1000);
      seconds = accumulated + currentSegment;
    } else {
      seconds = accumulated;
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  }, []);

  // Find active job (any job with running/paused session)
  const activeJob = useMemo(() => {
    if (!selectedQuote) return null;

    for (const job of selectedQuote.jobs) {
      if (job.activeSession) {
        return { ...job, session: job.activeSession };
      }
    }
    return null;
  }, [selectedQuote]);

  // Force re-render on tick
  void tick;

  return (
    <div className="flex-1 overflow-auto bg-white text-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
        {/* Work Center Selector */}
        <WorkCenterSelector
          workCenters={WORK_CENTERS}
          selected={selectedWorkCenters}
          counts={workCenterCounts}
          onToggle={handleToggleWorkCenter}
        />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden">
        {loading && quotes.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : error ? (
          <div className="flex-1 p-4">
            <div className="bg-red-100 border border-red-700 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchQueue}
                className="mt-2 px-3 py-1 bg-red-700 rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        ) : quotes.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-lg">No projects need your work</p>
            <p className="text-sm mt-1">
              {selectedWorkCenters.length === 0
                ? "Select work centers above"
                : "All caught up!"}
            </p>
          </div>
        ) : (
          <>
            {/* Quote Queue (Left Panel) */}
            <div className="w-80 border-r border-gray-200 overflow-y-auto">
              <QuoteQueue
                quotes={quotes}
                selectedQuote={selectedQuote}
                onSelectQuote={handleSelectQuote}
                selectedWorkCenters={selectedWorkCenters}
              />
            </div>

            {/* Quote Detail (Right Panel) */}
            <div className="flex-1 overflow-y-auto pb-24">
              {selectedQuote ? (
                <QuoteDetail
                  quote={selectedQuote}
                  selectedJobs={selectedJobs}
                  onToggleJob={handleToggleJob}
                  onSelectAllJobs={handleSelectAllJobs}
                  selectedWorkCenters={selectedWorkCenters}
                  formatElapsed={formatElapsed}
                  onStartJob={handleStartJob}
                  onPauseJob={handlePauseJob}
                  onResumeJob={handleResumeJob}
                  onCompleteJob={handleCompleteJob}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>Select a project to view details</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Pipeline Panel (if we have items) */}
      {pipeline.length > 0 && !selectedQuote && (
        <div className="border-t border-gray-200 bg-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-500 mb-2">
            Coming Up ({pipeline.length})
          </h3>
          <div className="flex gap-2 overflow-x-auto">
            {pipeline.slice(0, 5).map((item) => (
              <div
                key={item.jobNumber}
                className="flex-shrink-0 bg-gray-700/50 rounded-lg px-3 py-2 text-xs"
              >
                <div className="font-mono text-gray-900">{item.jobNumber}</div>
                <div className="text-gray-500">
                  {item.currentOperation} → {item.nextOperation}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Bar */}
      {selectedQuote && (
        <ProductionActionBar
          activeJob={activeJob}
          selectedJob={
            selectedJobs.length === 1
              ? selectedQuote.jobs.find((j) => j.jobNumber === selectedJobs[0])
              : null
          }
          selectedJobs={selectedJobs}
          quote={selectedQuote}
          activeSessions={
            selectedQuote.jobs.reduce((acc, job) => {
              if (job.activeSession) {
                acc[job.jobId] = job.activeSession;
              }
              return acc;
            }, {})
          }
          onStart={handleStartJob}
          onPause={handlePauseJob}
          onResume={handleResumeJob}
          onComplete={handleCompleteJob}
          formatElapsed={formatElapsed}
          workCenter={selectedWorkCenters[0]}
          selectedWorkCenters={selectedWorkCenters}
        />
      )}
    </div>
  );
}
